/**
 * Minimal vCard 3.0 builder for the Account page's "Add to contacts" links.
 *
 * Dependency-free and tiny: produces a string we embed in a `data:text/vcard`
 * anchor so a tap imports the agent (name, org, email, SMS number) as a contact.
 * We build it dynamically rather than shipping a static `.vcf` because the email
 * domain and phone number are stage-specific. Field values are escaped per
 * RFC 6350 (backslash, comma, semicolon, newline).
 */
export interface VCardInput {
  readonly name: string;
  readonly org: string;
  readonly title?: string;
  readonly email?: string;
  /** Phone number in E.164. */
  readonly tel?: string;
  readonly url?: string;
}

function escapeValue(value: string): string {
  return value.replace(/([\\,;])/g, '\\$1').replace(/\n/g, '\\n');
}

/** Build an RFC-6350 vCard 3.0 document (CRLF-separated) from `input`. */
export function buildVCard(input: VCardInput): string {
  const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
  lines.push(`FN:${escapeValue(input.name)}`);
  lines.push(`N:${escapeValue(input.name)};;;;`);
  lines.push(`ORG:${escapeValue(input.org)}`);
  if (input.title !== undefined) {
    lines.push(`TITLE:${escapeValue(input.title)}`);
  }
  if (input.email !== undefined) {
    lines.push(`EMAIL;TYPE=INTERNET:${escapeValue(input.email)}`);
  }
  if (input.tel !== undefined) {
    lines.push(`TEL;TYPE=CELL,VOICE:${escapeValue(input.tel)}`);
  }
  if (input.url !== undefined) {
    lines.push(`URL:${escapeValue(input.url)}`);
  }
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

/** Encode a vCard string as a downloadable `data:` URI for an anchor `href`. */
export function vCardDataUri(vcard: string): string {
  return `data:text/vcard;charset=utf-8,${encodeURIComponent(vcard)}`;
}
